import {isBoolean, some, orderBy, debounce} from 'lodash';
import {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {matchPath} from 'react-router';
import {useLocation} from 'react-router-dom';
import {useSocketEvent} from 'socket.io-react-hook';

import {sessionApi} from 'api';
import {STATUS_CODES} from 'constants/http';
import {ROUTE_PATH} from 'constants/routes';
import {UserContext} from 'context';
import {SocketEvent, SocketManagerEvent} from 'enums';
import {useSocketEventCallback, useIsChangedTo} from 'hooks';
import {OnlineSession, OnlineSessionsApiPayload, SessionStatus} from 'interfaces';
import {onlineSessionsStore} from 'services';
import {httpUtils, logger, userAuthUtils} from 'utils';

const SCREEN_RECORDER_LAST_UPDATE_ONLINE_TIME = 1000 * 12; // 12 sec
const NO_ONLINE_SESSIONS_ROUTES = [ROUTE_PATH.SESSION.FILE_REDIRECT];
const STATUSES_ORDER = [
    SessionStatus.New,
    SessionStatus.Calling,
    SessionStatus.ClientLeft,
    SessionStatus.PreCall,
    SessionStatus.InSession,
    SessionStatus.OnHold,
    SessionStatus.SessionEnded
];

type ScreenRecorderStateChanges = {
    version: string;
    isValidVersion: boolean;
    lastUpdate: string;
}

export const OnlineSessionsLoader: React.FC = () => {
    const {socket, settings, auth} = useContext(UserContext);
    const permissions = useMemo(() => userAuthUtils.getUserPermissions(), []);
    const hasOnlineSessionsPermission = useMemo(() => permissions?.online?.enabled, [permissions]);
    const [fullRefreshRequired, setFullRefreshRequired] = useState(false);
    const {pathname} = useLocation();

    const noOnlineSessionsRoute = useMemo(() => some(NO_ONLINE_SESSIONS_ROUTES, (route) => matchPath(pathname, {path: route})), [pathname]);
    const shouldLoadOnlyOwnOnlineSessions = useMemo(() => auth?.sessionAccessOnly, [auth]);
    const openedWithOnlineSessionsRoute = useIsChangedTo(noOnlineSessionsRoute, false);
    const screenRecorderOnlineTimer = useRef<NodeJS.Timeout>();
    const throttledSessionsRef = useRef<OnlineSession[]>([]);
    const canSeeOnlineSessions = useMemo(() => !noOnlineSessionsRoute && hasOnlineSessionsPermission,
        [hasOnlineSessionsPermission, noOnlineSessionsRoute]);

    const {sendMessage: sendRequestScreenRecorderState} = useSocketEvent(socket, SocketEvent.ScreenRecorderStateRequest);

    const sortSessions = useCallback((sessions: OnlineSession[]) =>
        orderBy(sessions, [
            (session) => STATUSES_ORDER.indexOf(session.status),
            (session) => session.startDate
        ], ['asc', 'desc']), []);

    const handleSessionsChangedThrottle = useCallback(() => {
        onlineSessionsStore.mergeValue({sessions: sortSessions(throttledSessionsRef.current)});
    }, [sortSessions]);

    const setSessionsDebounced = useMemo(() => debounce(handleSessionsChangedThrottle, 500, {maxWait: 1000}),
        [handleSessionsChangedThrottle]);

    const handleSessionsChanged = useCallback((sessions: OnlineSession[]) => {
        throttledSessionsRef.current = sessions;
        setSessionsDebounced();
    }, [setSessionsDebounced]);

    const isValidScreenRecorderLastUpdate = useCallback((lastUpdate: string) =>
        lastUpdate && +new Date(lastUpdate) + SCREEN_RECORDER_LAST_UPDATE_ONLINE_TIME > +new Date(), []);

    const requestScreenRecorderState = useCallback(() => {
        if (settings.userSession?.requireScreenRecorder) {
            sendRequestScreenRecorderState(null);

        }
    }, [settings.userSession?.requireScreenRecorder, sendRequestScreenRecorderState]);

    const resetScreenRecorderOnlineTimer = useCallback(() => {
        if (screenRecorderOnlineTimer.current) {
            clearTimeout(screenRecorderOnlineTimer.current);
        }
    }, []);

    const refreshScreenRecorderOnlineTimer = useCallback((lastUpdate: string) => {
        resetScreenRecorderOnlineTimer();
        const onlineTimeLeft = +new Date(lastUpdate) + SCREEN_RECORDER_LAST_UPDATE_ONLINE_TIME - +new Date();
        if (onlineTimeLeft > 0) {
            screenRecorderOnlineTimer.current = setTimeout(() => {
                onlineSessionsStore.mergeValue({screenRecorderState: {isOnline: false}});
            }, onlineTimeLeft);
        } else {
            onlineSessionsStore.mergeValue({screenRecorderState: {isOnline: false}});
        }
    }, [resetScreenRecorderOnlineTimer]);

    const handleScreenRecorderStateChange = useCallback((changes: ScreenRecorderStateChanges) => {
        const currentState = onlineSessionsStore.getValue().screenRecorderState;
        const isValidVersion = isBoolean(changes?.isValidVersion) ? changes?.isValidVersion : currentState?.isValidVersion;
        const isOnline = isValidVersion && isValidScreenRecorderLastUpdate(changes?.lastUpdate);
        const version = changes?.version || currentState?.version;
        if (changes.lastUpdate) {
            refreshScreenRecorderOnlineTimer(changes.lastUpdate);
        }
        onlineSessionsStore.mergeValue({screenRecorderState: {isValidVersion, isOnline, version}});
    }, [isValidScreenRecorderLastUpdate, refreshScreenRecorderOnlineTimer]);

    useSocketEventCallback(socket, SocketEvent.OnlineSessionChanged, useCallback((session: OnlineSession) => {
        logger.debug(`Received socket event: ${SocketEvent.OnlineSessionChanged}`, session);
        const prevSessions = throttledSessionsRef.current;
        const isExisting = some(prevSessions, {id: session.id});
        handleSessionsChanged(
            isExisting ?
                prevSessions.map((prevSession) => prevSession.id === session.id ? session : prevSession) :
                [...prevSessions, session]
        );
    }, [handleSessionsChanged]));

    useSocketEventCallback(socket, SocketEvent.OnlineSessionDeleted, useCallback(({id}: {id: string}) => {
        logger.debug(`Received socket event: ${SocketEvent.OnlineSessionDeleted}`, id);
        const prevSessions = throttledSessionsRef.current;
        const isExisting = some(prevSessions, {id});
        if (isExisting) {
            handleSessionsChanged(prevSessions.filter((prevSession) => prevSession.id !== id));
        }
    }, [handleSessionsChanged]));

    useSocketEventCallback(socket, SocketEvent.ProfileAssignmentChanged, useCallback(() => {
        setFullRefreshRequired(true);
    }, []));

    useSocketEventCallback(socket, SocketEvent.Authenticated, useCallback((payload: {screenRecorderState: ScreenRecorderStateChanges}) => {
        setFullRefreshRequired(true);
        if (payload?.screenRecorderState) {
            handleScreenRecorderStateChange(payload.screenRecorderState);
        }
    }, [handleScreenRecorderStateChange]), {emitOnInit: true});

    useSocketEventCallback(socket, SocketManagerEvent.ReconnectFailed, useCallback(() => {
        onlineSessionsStore.resetValue();
        onlineSessionsStore.mergeValue({error: new Error('Failed connection to socket server')});
    }, []));

    useSocketEventCallback(socket, SocketEvent.Disconnect, useCallback(() => {
        resetScreenRecorderOnlineTimer();
    }, [resetScreenRecorderOnlineTimer]));

    useSocketEventCallback(socket, SocketEvent.ScreenRecorderStateChanged,
        useCallback((changes: ScreenRecorderStateChanges) => {
            if (changes) {
                logger.debug(`Received socket event: ${SocketEvent.ScreenRecorderStateChanged}`, changes);
                handleScreenRecorderStateChange(changes);
            }
        }, [handleScreenRecorderStateChange]));

    const getOnlineSessions = useCallback(() => {
        if (!canSeeOnlineSessions) {
            return;
        }
        httpUtils.retryRequest(
            shouldLoadOnlyOwnOnlineSessions ? sessionApi.getMyOnlineSessions() : sessionApi.getOnlineSessions(),
            {notRetryOnStatus: [STATUS_CODES.UNAUTHORIZED]}
        ).then(({sessions, ...rest}: OnlineSessionsApiPayload) => {
            handleSessionsChanged(sessions);
            onlineSessionsStore.mergeValue({...rest, sessionsLoaded: true, error: null});
        }).catch((error) => {
            handleSessionsChanged([]);
            onlineSessionsStore.resetValue();
            onlineSessionsStore.mergeValue({error});
        });
    }, [shouldLoadOnlyOwnOnlineSessions, canSeeOnlineSessions, handleSessionsChanged]);

    useEffect(() => {
        if (fullRefreshRequired) {
            setFullRefreshRequired(false);
            requestScreenRecorderState();
            getOnlineSessions();
        }
    }, [getOnlineSessions, requestScreenRecorderState, fullRefreshRequired]);

    useEffect(() => resetScreenRecorderOnlineTimer, [resetScreenRecorderOnlineTimer]);

    useEffect(() => {
        if (openedWithOnlineSessionsRoute) {
            setFullRefreshRequired(true);
        }
    }, [openedWithOnlineSessionsRoute]);

    useEffect(() => () => {
        onlineSessionsStore.resetValue();
        throttledSessionsRef.current = [];
    }, []);

    return null;
};
